import { type CallsClient, createCallsClient } from '@wtfalch/calls';
import type {
  CallEndpointAdapter,
  CallEndpointOptions,
  CallEndpointStore,
} from '@wtfalch/calls/browser';

import { announceCallJoined } from './ringing.ts';

export type CallEndpointRequestOptions = Pick<
  CallEndpointOptions,
  'baseUrl' | 'token' | 'deviceId'
>;

/**
 * What a room or calendar entry point needs to let a person start or join a
 * call (agora#340, agora#341): the control client -- `createSession`,
 * `roomStatus`, `joinAdmission` -- plus the media adapter and endpoint store
 * `useCallEndpoint` (`./useCallEndpoint.ts`) needs to actually join one.
 * Bundled behind this one optional prop so a caller that has not wired a
 * concrete `CallsClient`/adapter/store for `@wtfalch/calls` yet -- there is
 * none in `App.tsx` as of this writing -- simply never draws the call
 * affordance, rather than this component inventing a base URL or a store.
 */
export type RoomCallBinding = {
  client: CallsClient;
  adapter: CallEndpointAdapter;
  store: CallEndpointStore;
  fetchImpl?: typeof fetch;
  /** Where and as whom the engine reaches the calls service: the `/_calls`
   *  base URL, the member bearer, the per-browser device id. */
  endpointOptions?: CallEndpointRequestOptions;
};

/**
 * The binding for one join, under a device id nobody has used before. The
 * calls service keeps a seat's row after its holder leaves and refuses a
 * second seat for the same person and device in that call, so a rejoin under
 * the browser's own id is a conflict for as long as the call lives. The join,
 * the engine and the leave of one seat all go through the binding returned
 * here, so they agree on the id.
 */
export function withFreshDevice(calls: RoomCallBinding): RoomCallBinding {
  const baseUrl = calls.endpointOptions?.baseUrl;
  if (!baseUrl) return calls;
  const endpointOptions = { ...calls.endpointOptions, baseUrl, deviceId: crypto.randomUUID() };
  const client = createCallsClient({
    ...endpointOptions,
    ...(calls.fetchImpl ? { fetch: calls.fetchImpl } : {}),
  });
  return { ...calls, client, endpointOptions };
}

/** How long a freshly created session stays open for admission -- generous
 *  enough to cover a long meeting without being unbounded. `CallsClient`'s
 *  own `endSession` still closes one early once the last person leaves. */
const SESSION_WINDOW_MS = 12 * 60 * 60 * 1000;

/** A fixed cap rather than a per-room member count this client has no cheap
 *  way to read here; the service is the one place that actually enforces
 *  it. Matches the service's own cap (calls#95) -- a session created with a
 *  higher number here would still be refused there, just later and less
 *  clearly, once the 26th person tried to join. */
const SESSION_CAPACITY = 25;

/** The call's window has not opened yet. */
export class CallNotOpenError extends Error {
  constructor(readonly opensAt: number) {
    super(
      `This call opens at ${new Date(opensAt).toLocaleTimeString([], { timeStyle: 'short' })}.`,
    );
  }
}

/**
 * Starts a new call for `roomId`, or joins one already live there --
 * `CallsClient.roomStatus` is the one call that tells the two apart. Either
 * way the caller still has to be admitted (`joinAdmission`): creating a
 * session reserves it for the room but does not by itself seat its creator,
 * the same as every other participant.
 */
export async function startOrJoinRoomCall(
  client: CallsClient,
  roomId: string,
  video = false,
  window?: { validFrom: number; expiresAt: number },
): Promise<{ sessionId: string; created: boolean }> {
  const status = await client.roomStatus(roomId);
  const sessionId = status.live ? status.sessionId : crypto.randomUUID();
  if (!status.live) {
    const now = Date.now();
    // An event's call has its own window, never the huddle's twelve hours.
    if (window && now < window.validFrom) throw new CallNotOpenError(window.validFrom);
    if (window && now >= window.expiresAt) throw new Error('This call has closed.');
    await client.createSession({
      sessionId,
      roomId,
      validFrom: window?.validFrom ?? now,
      expiresAt: window?.expiresAt ?? now + SESSION_WINDOW_MS,
      capacity: SESSION_CAPACITY,
      requestId: crypto.randomUUID(),
    });
  }
  await client.joinAdmission(sessionId, { video, requestId: crypto.randomUUID() });
  announceCallJoined(roomId);
  return { sessionId, created: !status.live };
}
